/**
 * CMS-driven pages: campaign landing pages (/lp/:slug) and standalone form pages
 * (/formularz/:key). Both render whatever block sequence the CMS supplies, using
 * the same section grammar as the hand-built views.
 */
import { el, fromTrustedHtml } from "../core/dom.js";
import type { AppContext } from "../core/context.js";
import type { RouteMatch } from "../core/routes.js";
import { applyPageSeo } from "../core/seo.js";
import { breadcrumbs, markerList, sectionHead } from "../components/common.js";
import { serviceCard, caseCard } from "../components/cards.js";
import { contactForm } from "../components/forms.js";
import type { FormDefinition, PageBlock } from "../data/types.js";
import { notFoundView } from "./status.js";

export async function landingView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const slug = match.params.slug ?? "";
	const page = await ctx.gateway.page(slug, match.query.preview === "true");
	if (!page) return notFoundView(ctx);

	applyPageSeo(ctx, {
		title: `${page.title} — ${copy.brand.name}`,
		description: page.seo.description || copy.home.heroLead,
	});

	const view = el("div", { class: "mb-view mb-view--landing" });
	view.appendChild(
		el(
			"section",
			{ class: "mb-section mb-section--glow" },
			el(
				"div",
				{ class: "mb-section__inner" },
				breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: page.title }]),
				el("h1", { class: "mb-pagehead__title" }, page.title),
			),
		),
	);
	for (const block of page.blocks) {
		const rendered = renderBlock(ctx, block);
		if (rendered) view.appendChild(rendered);
	}
	return view;
}

export async function formPageView(ctx: AppContext, match: RouteMatch): Promise<HTMLElement> {
	const copy = ctx.copy();
	const key = match.params.key ?? "contact";
	const [definition, categories] = await Promise.all([
		ctx.gateway.formDefinition(key),
		ctx.gateway.serviceCategories(),
	]);
	if (!definition) return notFoundView(ctx);

	applyPageSeo(ctx, {
		title: `${definition.title} — ${copy.brand.name}`,
		description: copy.contact.lead,
		noindex: true,
	});

	return el(
		"div",
		{ class: "mb-view mb-view--form" },
		el(
			"section",
			{ class: "mb-section mb-section--white" },
			el(
				"div",
				{ class: "mb-section__inner mb-contact__grid" },
				el(
					"div",
					null,
					breadcrumbs(ctx, [{ label: copy.nav.start, key: "home" }, { label: definition.title }]),
					el("h1", { class: "mb-pagehead__title" }, definition.title),
					el("p", { class: "mb-pagehead__lead" }, copy.contact.lead),
					markerList(copy.contact.points),
				),
				// The contact form covers every field the CMS definitions use today;
				// a definition with extra fields still submits under its own key.
				contactForm(ctx, categories),
			),
		),
	);
}

function renderBlock(ctx: AppContext, block: PageBlock): HTMLElement | null {
	switch (block.__typename) {
		case "HeroBlock": {
			return el(
				"section",
				{ class: "mb-section mb-section--white" },
				el(
					"div",
					{ class: "mb-section__inner" },
					el("h2", { class: "mb-blocktitle mb-blocktitle--lead" }, block.heading),
					block.subheading ? el("p", { class: "mb-prose" }, block.subheading) : null,
					block.cta
						? el(
								"a",
								{
									class: "mb-cta mb-cta--accent",
									href: block.cta.href,
									...(block.cta.external ? { rel: "external noopener", target: "_blank" } : {}),
								},
								block.cta.label,
							)
						: null,
				),
			);
		}
		case "RichTextBlock": {
			const wrap = el("section", { class: "mb-section mb-section--white" });
			const inner = el("div", { class: "mb-section__inner mb-prose mb-prose--article" });
			inner.appendChild(fromTrustedHtml(block.html));
			wrap.appendChild(inner);
			return wrap;
		}
		case "ServiceGridBlock": {
			const grid = el("div", { class: "mb-grid mb-grid--3" });
			for (const service of block.services) grid.appendChild(serviceCard(ctx, service));
			return el(
				"section",
				{ class: "mb-section mb-section--paper" },
				el("div", { class: "mb-section__inner" }, grid),
			);
		}
		case "CaseStudyListBlock": {
			const grid = el("div", { class: "mb-grid mb-grid--3" });
			for (const study of block.caseStudies) grid.appendChild(caseCard(ctx, study));
			return el(
				"section",
				{ class: "mb-section mb-section--white" },
				el("div", { class: "mb-section__inner" }, grid),
			);
		}
		case "ProcessStepsBlock": {
			const grid = el("div", { class: "mb-grid mb-grid--4" });
			block.steps.forEach((step, index) => {
				grid.appendChild(
					el(
						"article",
						{ class: "mb-step" },
						el("span", { class: "mb-step__num" }, String(index + 1).padStart(2, "0")),
						el("h3", { class: "mb-step__title" }, step.title),
						el("p", { class: "mb-step__desc" }, step.description),
					),
				);
			});
			return el(
				"section",
				{ class: "mb-section mb-section--paper" },
				el("div", { class: "mb-section__inner" }, grid),
			);
		}
		case "FormBlock":
			return formBlock(ctx, block.form);
		case "CtaBlock": {
			return el(
				"section",
				{ class: "mb-section mb-section--glow" },
				el(
					"div",
					{ class: "mb-section__inner mb-endcta" },
					el("h2", { class: "mb-endcta__title" }, block.heading),
					el(
						"div",
						{ class: "mb-endcta__actions" },
						el(
							"a",
							{
								class: "mb-cta mb-cta--accent",
								href: block.action.href,
								...(block.action.external ? { rel: "external noopener", target: "_blank" } : {}),
							},
							block.action.label,
						),
					),
				),
			);
		}
		default:
			return null;
	}
}

function formBlock(ctx: AppContext, form: FormDefinition): HTMLElement {
	return el(
		"section",
		{ class: "mb-section mb-section--white" },
		el(
			"div",
			{ class: "mb-section__inner" },
			sectionHead(ctx, { title: form.title }),
			el(
				"a",
				{ class: "mb-cta mb-cta--accent", href: ctx.href("formPage", { key: form.key }) },
				form.submitLabel,
			),
		),
	);
}
